import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';

function CurlRunner() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  
  const [curlCommand, setCurlCommand] = useState('');
  const [isRunning, setIsRunning] = useState(false);
  const [response, setResponse] = useState(null);
  const [responseRaw, setResponseRaw] = useState('');
  const [responseStatus, setResponseStatus] = useState(null);
  const [responseHeaders, setResponseHeaders] = useState({});
  const [logs, setLogs] = useState([]);
  const [method, setMethod] = useState('POST');
  const [url, setUrl] = useState('');
  const [headers, setHeaders] = useState([{ key: 'Content-Type', value: 'application/json' }]);
  const [body, setBody] = useState('');
  const [bodyType, setBodyType] = useState('json');
  const [formData, setFormData] = useState([{ key: '', value: '' }]);
  
  const logContainerRef = useRef(null);

  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const addLog = (message, type = 'info') => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [...prev, { timestamp, message, type }]);
    console.log(`[${timestamp}] [${type}] ${message}`);
  };

  // =====================================================
  // API COLLECTION - All Google Apps Script Endpoints
  // =====================================================
  
  const apiCollection = {
    // Group Management
    groups: {
      name: 'Get All Groups',
      description: 'Fetch all groups with their details',
      method: 'GET',
      endpoint: '/?type=groups',
    },
    groupsAndQuestions: {
      name: 'Get Groups & Questions',
      description: 'Fetch groups for question distribution',
      method: 'GET',
      endpoint: '/?type=groups_and_questions',
    },
    getAllChatIds: {
      name: 'Get All Chat IDs',
      description: 'Fetch all user and group chat IDs',
      method: 'GET',
      endpoint: '/?type=all',
    },
    chatIdsAndQuestions: {
      name: 'Get Chat IDs & Questions',
      description: 'Fetch all chat IDs and poll questions',
      method: 'GET',
      endpoint: '/?type=chatids_and_questions',
    },
    
    // User Management
    userInfo: {
      name: 'Get User Info',
      description: 'Get detailed user information with stats',
      method: 'GET',
      endpoint: '/?type=user_info&chatid={chatid}',
    },
    groupInfo: {
      name: 'Get Group Info',
      description: 'Get detailed group information with stats',
      method: 'GET',
      endpoint: '/?type=group_info&chatid={chatid}',
    },
    
    // Response Data
    combinedResponses: {
      name: 'Get Combined Responses',
      description: 'Fetch all user and group responses (current & weekly)',
      method: 'GET',
      endpoint: '/?type=combined_responses_and_ids',
    },
    combinedResponsesGroups: {
      name: 'Get Combined Responses & Groups',
      description: 'Fetch all responses with user/group details',
      method: 'GET',
      endpoint: '/?type=combined_responses_and_groups',
    },
    
    // Poll Management
    pollAndSettings: {
      name: 'Get Poll & Settings',
      description: 'Get poll metadata with group settings',
      method: 'GET',
      endpoint: '/?type=poll_and_settings&pollId={pollId}&voterChatId={voterChatId}',
    },
    
    // Statistics
    stats: {
      name: 'Get Statistics',
      description: 'Get overall system statistics',
      method: 'GET',
      endpoint: '/?type=stats',
    },
    
    // Leaderboard
    leaderboard: {
      name: 'Get Leaderboard',
      description: 'Get daily leaderboard for users and groups',
      method: 'GET',
      endpoint: '/?type=leaderboard&date={date}',
    },
    weeklyRank: {
      name: 'Get Weekly Rank',
      description: 'Get weekly rank for a specific user or group',
      method: 'GET',
      endpoint: '/?type=weekly_rank&chatid={chatid}&type={user|group}',
    },
    
    // Sheet Data
    sheetData: {
      name: 'Get Sheet Data',
      description: 'Fetch data from any sheet by name',
      method: 'GET',
      endpoint: '/?type=sheet&name={sheetName}',
    },
    
    // =====================================================
    // WRITE OPERATIONS (POST)
    // =====================================================
    
    checkAndAdd: {
      name: 'Check & Add User/Group',
      description: 'Check if user/group exists, update or add with latest info',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'check_and_add',
        id: '{chatId}',
        isGroup: '{true|false}',
        name: '{name}',
        username: '{username}',
        chatType: '{group|supergroup|channel}'
      }
    },
    
    userGroupResponse: {
      name: 'Save User/Group Response',
      description: 'Save a quiz response for a user and group',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'user_and_group_response',
        userId: '{userId}',
        groupId: '{groupId}',
        isCorrect: '{true|false}',
        timestamp: '{timestamp}'
      }
    },
    
    pollQuestion: {
      name: 'Save Poll Question',
      description: 'Save poll metadata for quiz tracking',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'poll_question',
        pollIds: '{pollIds}',
        chatIds: '{chatIds}',
        question_id: '{questionId}',
        correctOption: '{A|B|C|D}',
        timestamp: '{timestamp}',
        chapter: '{chapter}',
        source: '{source}'
      }
    },
    
    updateLanguage: {
      name: 'Update Group Language',
      description: 'Update language setting for a group',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'update_language',
        chatId: '{chatId}',
        language: '{english|hindi}'
      }
    },
    
    updateFunny: {
      name: 'Update Group Funny Mode',
      description: 'Update funny mode setting for a group',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'update_funny',
        chatId: '{chatId}',
        status: '{allow|disallow}'
      }
    },
    
    removeChatIds: {
      name: 'Remove Users/Groups',
      description: 'Remove users or groups from the system',
      method: 'POST',
      endpoint: '/',
      body: {
        type: 'remove_chatids',
        chatIds: '{chatId or [chatId1, chatId2]}',
        isGroup: '{true|false}'
      }
    },
    
    // =====================================================
    // TELEGRAM BOT API (Direct)
    // =====================================================
    
    telegramSendMessage: {
      name: 'Telegram: Send Message',
      description: 'Send a message to a chat via Telegram Bot',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendMessage',
      body: {
        chat_id: '{chatId}',
        text: '{message}',
        parse_mode: '{HTML|Markdown}',
        disable_web_page_preview: '{true|false}',
        disable_notification: '{true|false}'
      }
    },
    
    telegramEditMessage: {
      name: 'Telegram: Edit Message',
      description: 'Edit an existing message',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/editMessageText',
      body: {
        chat_id: '{chatId}',
        message_id: '{messageId}',
        text: '{newText}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramDeleteMessage: {
      name: 'Telegram: Delete Message',
      description: 'Delete a message',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/deleteMessage',
      body: {
        chat_id: '{chatId}',
        message_id: '{messageId}'
      }
    },
    
    telegramGetChat: {
      name: 'Telegram: Get Chat Info',
      description: 'Get information about a chat',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getChat?chat_id={chatId}',
    },
    
    telegramGetChatMembersCount: {
      name: 'Telegram: Get Members Count',
      description: 'Get chat members count',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getChatMembersCount?chat_id={chatId}',
    },
    
    telegramGetChatMember: {
      name: 'Telegram: Get Chat Member',
      description: 'Get information about a chat member',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getChatMember?chat_id={chatId}&user_id={userId}',
    },
    
    telegramPromoteChatMember: {
      name: 'Telegram: Promote Member',
      description: 'Promote a user to admin in a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/promoteChatMember',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}',
        can_change_info: '{true|false}',
        can_post_messages: '{true|false}',
        can_edit_messages: '{true|false}',
        can_delete_messages: '{true|false}',
        can_invite_users: '{true|false}',
        can_restrict_members: '{true|false}',
        can_pin_messages: '{true|false}',
        can_promote_members: '{true|false}'
      }
    },
    
    telegramDemoteChatMember: {
      name: 'Telegram: Demote Member',
      description: 'Remove admin privileges from a user',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/promoteChatMember',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}',
        can_change_info: 'false',
        can_post_messages: 'false',
        can_edit_messages: 'false',
        can_delete_messages: 'false',
        can_invite_users: 'false',
        can_restrict_members: 'false',
        can_pin_messages: 'false',
        can_promote_members: 'false'
      }
    },
    
    telegramRestrictChatMember: {
      name: 'Telegram: Restrict Member',
      description: 'Restrict a user in a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/restrictChatMember',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}',
        permissions: '{JSON permissions}',
        until_date: '{unixTimestamp}'
      }
    },
    
    telegramBanChatMember: {
      name: 'Telegram: Ban Member',
      description: 'Ban a user from a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/banChatMember',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}',
        until_date: '{unixTimestamp}',
        revoke_messages: '{true|false}'
      }
    },
    
    telegramUnbanChatMember: {
      name: 'Telegram: Unban Member',
      description: 'Unban a user from a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/unbanChatMember',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}',
        only_if_banned: '{true|false}'
      }
    },
    
    telegramSetChatPermissions: {
      name: 'Telegram: Set Chat Permissions',
      description: 'Set default permissions for all members',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/setChatPermissions',
      body: {
        chat_id: '{chatId}',
        permissions: '{JSON permissions}'
      }
    },
    
    telegramSetChatTitle: {
      name: 'Telegram: Set Chat Title',
      description: 'Change group/supergroup title',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/setChatTitle',
      body: {
        chat_id: '{chatId}',
        title: '{newTitle}'
      }
    },
    
    telegramSetChatDescription: {
      name: 'Telegram: Set Chat Description',
      description: 'Change group/supergroup description',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/setChatDescription',
      body: {
        chat_id: '{chatId}',
        description: '{newDescription}'
      }
    },
    
    telegramSetChatPhoto: {
      name: 'Telegram: Set Chat Photo',
      description: 'Set group/supergroup profile photo',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/setChatPhoto',
      body: {
        chat_id: '{chatId}',
        photo: '{photoFileId}'
      }
    },
    
    telegramDeleteChatPhoto: {
      name: 'Telegram: Delete Chat Photo',
      description: 'Delete group/supergroup profile photo',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/deleteChatPhoto',
      body: {
        chat_id: '{chatId}'
      }
    },
    
    telegramPinChatMessage: {
      name: 'Telegram: Pin Message',
      description: 'Pin a message in a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/pinChatMessage',
      body: {
        chat_id: '{chatId}',
        message_id: '{messageId}',
        disable_notification: '{true|false}'
      }
    },
    
    telegramUnpinChatMessage: {
      name: 'Telegram: Unpin Message',
      description: 'Unpin a message in a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/unpinChatMessage',
      body: {
        chat_id: '{chatId}',
        message_id: '{messageId}'
      }
    },
    
    telegramUnpinAllChatMessages: {
      name: 'Telegram: Unpin All Messages',
      description: 'Unpin all messages in a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/unpinAllChatMessages',
      body: {
        chat_id: '{chatId}'
      }
    },
    
    telegramLeaveChat: {
      name: 'Telegram: Leave Chat',
      description: 'Make bot leave a group/supergroup',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/leaveChat',
      body: {
        chat_id: '{chatId}'
      }
    },
    
    telegramGetChatAdministrators: {
      name: 'Telegram: Get Admins',
      description: 'Get list of chat administrators',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getChatAdministrators?chat_id={chatId}',
    },
    
    telegramSendPoll: {
      name: 'Telegram: Send Poll',
      description: 'Send a poll to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendPoll',
      body: {
        chat_id: '{chatId}',
        question: '{question}',
        options: '{["option1","option2","option3","option4"]}',
        is_anonymous: '{true|false}',
        type: '{regular|quiz}',
        allows_multiple_answers: '{true|false}',
        correct_option_id: '{0|1|2|3}',
        explanation: '{explanation}',
        explanation_parse_mode: '{HTML|Markdown}',
        open_period: '{seconds}',
        close_date: '{unixTimestamp}'
      }
    },
    
    telegramSendPhoto: {
      name: 'Telegram: Send Photo',
      description: 'Send a photo to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendPhoto',
      body: {
        chat_id: '{chatId}',
        photo: '{photoFileId|photoUrl}',
        caption: '{caption}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramSendDocument: {
      name: 'Telegram: Send Document',
      description: 'Send a document to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendDocument',
      body: {
        chat_id: '{chatId}',
        document: '{fileId|fileUrl}',
        caption: '{caption}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramSendSticker: {
      name: 'Telegram: Send Sticker',
      description: 'Send a sticker to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendSticker',
      body: {
        chat_id: '{chatId}',
        sticker: '{stickerFileId}'
      }
    },
    
    telegramSendVideo: {
      name: 'Telegram: Send Video',
      description: 'Send a video to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendVideo',
      body: {
        chat_id: '{chatId}',
        video: '{videoFileId|videoUrl}',
        caption: '{caption}',
        duration: '{seconds}',
        width: '{width}',
        height: '{height}',
        supports_streaming: '{true|false}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramSendAudio: {
      name: 'Telegram: Send Audio',
      description: 'Send an audio file to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendAudio',
      body: {
        chat_id: '{chatId}',
        audio: '{audioFileId|audioUrl}',
        caption: '{caption}',
        duration: '{seconds}',
        performer: '{performer}',
        title: '{title}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramSendVoice: {
      name: 'Telegram: Send Voice',
      description: 'Send a voice note to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendVoice',
      body: {
        chat_id: '{chatId}',
        voice: '{voiceFileId|voiceUrl}',
        caption: '{caption}',
        duration: '{seconds}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramSendLocation: {
      name: 'Telegram: Send Location',
      description: 'Send a location to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendLocation',
      body: {
        chat_id: '{chatId}',
        latitude: '{latitude}',
        longitude: '{longitude}',
        live_period: '{seconds}',
        horizontal_accuracy: '{accuracy}'
      }
    },
    
    telegramSendVenue: {
      name: 'Telegram: Send Venue',
      description: 'Send a venue to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendVenue',
      body: {
        chat_id: '{chatId}',
        latitude: '{latitude}',
        longitude: '{longitude}',
        title: '{title}',
        address: '{address}',
        foursquare_id: '{foursquareId}'
      }
    },
    
    telegramSendContact: {
      name: 'Telegram: Send Contact',
      description: 'Send a contact to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendContact',
      body: {
        chat_id: '{chatId}',
        phone_number: '{phoneNumber}',
        first_name: '{firstName}',
        last_name: '{lastName}',
        vcard: '{vcardData}'
      }
    },
    
    telegramSendDice: {
      name: 'Telegram: Send Dice',
      description: 'Send a dice to a chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendDice',
      body: {
        chat_id: '{chatId}',
        emoji: '{🎲|🎯|🏀|⚽|🎳|🎰}'
      }
    },
    
    telegramForwardMessage: {
      name: 'Telegram: Forward Message',
      description: 'Forward a message from one chat to another',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/forwardMessage',
      body: {
        chat_id: '{targetChatId}',
        from_chat_id: '{sourceChatId}',
        message_id: '{messageId}',
        disable_notification: '{true|false}'
      }
    },
    
    telegramCopyMessage: {
      name: 'Telegram: Copy Message',
      description: 'Copy a message to another chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/copyMessage',
      body: {
        chat_id: '{targetChatId}',
        from_chat_id: '{sourceChatId}',
        message_id: '{messageId}',
        caption: '{newCaption}',
        parse_mode: '{HTML|Markdown}'
      }
    },
    
    telegramGetMe: {
      name: 'Telegram: Get Bot Info',
      description: 'Get information about the bot itself',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getMe',
    },
    
    telegramGetUpdates: {
      name: 'Telegram: Get Updates',
      description: 'Get incoming updates from Telegram',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getUpdates?offset={offset}&limit={limit}',
    },
    
    telegramSetWebhook: {
      name: 'Telegram: Set Webhook',
      description: 'Set webhook URL for receiving updates',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/setWebhook',
      body: {
        url: '{webhookUrl}',
        max_connections: '{maxConnections}',
        allowed_updates: '{["message","callback_query"]}'
      }
    },
    
    telegramDeleteWebhook: {
      name: 'Telegram: Delete Webhook',
      description: 'Delete webhook URL',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/deleteWebhook',
      body: {}
    },
    
    telegramGetWebhookInfo: {
      name: 'Telegram: Get Webhook Info',
      description: 'Get current webhook status',
      method: 'GET',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/getWebhookInfo',
    },
    
    telegramSendChatAction: {
      name: 'Telegram: Send Chat Action',
      description: 'Show typing/action status in chat',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/sendChatAction',
      body: {
        chat_id: '{chatId}',
        action: '{typing|upload_photo|record_video|upload_video|record_audio|upload_audio|upload_document|find_location|record_video_note|upload_video_note}'
      }
    },
    
    telegramExportChatInviteLink: {
      name: 'Telegram: Export Invite Link',
      description: 'Generate an invite link for a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/exportChatInviteLink',
      body: {
        chat_id: '{chatId}'
      }
    },
    
    telegramCreateChatInviteLink: {
      name: 'Telegram: Create Invite Link',
      description: 'Create a new invite link with expiration',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/createChatInviteLink',
      body: {
        chat_id: '{chatId}',
        name: '{linkName}',
        expire_date: '{unixTimestamp}',
        member_limit: '{maxUses}',
        creates_join_request: '{true|false}'
      }
    },
    
    telegramEditChatInviteLink: {
      name: 'Telegram: Edit Invite Link',
      description: 'Edit an existing invite link',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/editChatInviteLink',
      body: {
        chat_id: '{chatId}',
        invite_link: '{inviteLink}',
        name: '{newName}',
        expire_date: '{unixTimestamp}',
        member_limit: '{maxUses}',
        creates_join_request: '{true|false}'
      }
    },
    
    telegramRevokeChatInviteLink: {
      name: 'Telegram: Revoke Invite Link',
      description: 'Revoke an invite link',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/revokeChatInviteLink',
      body: {
        chat_id: '{chatId}',
        invite_link: '{inviteLink}'
      }
    },
    
    telegramApproveChatJoinRequest: {
      name: 'Telegram: Approve Join Request',
      description: 'Approve a user\'s join request to a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/approveChatJoinRequest',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}'
      }
    },
    
    telegramDeclineChatJoinRequest: {
      name: 'Telegram: Decline Join Request',
      description: 'Decline a user\'s join request to a group',
      method: 'POST',
      endpoint: 'https://api.telegram.org/bot{BOT_TOKEN}/declineChatJoinRequest',
      body: {
        chat_id: '{chatId}',
        user_id: '{userId}'
      }
    }
  };

  // =====================================================
  // API Quick Actions
  // =====================================================
  
  const loadApiEndpoint = (apiKey) => {
    const api = apiCollection[apiKey];
    if (!api) {
      addLog(`❌ API "${apiKey}" not found`, 'error');
      return;
    }
    
    // Determine the base URL (Google Script or Telegram)
    let baseUrl = 'https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec';
    
    if (apiKey.startsWith('telegram')) {
      baseUrl = 'https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko';
    }
    
    // Build the full URL
    let fullEndpoint = api.endpoint.replace('{BOT_TOKEN}', '7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko');
    let fullUrl = baseUrl + fullEndpoint;
    
    // Set method
    setMethod(api.method);
    setUrl(fullUrl);
    
    // Set body if it's a POST request
    if (api.method === 'POST' && api.body) {
      setBody(JSON.stringify(api.body, null, 2));
      setBodyType('json');
    }
    
    // Build headers
    const newHeaders = [{ key: 'Content-Type', value: 'application/json' }];
    if (apiKey.startsWith('telegram')) {
      newHeaders.push({ key: 'Content-Type', value: 'application/json' });
    }
    setHeaders(newHeaders);
    
    addLog(`📋 Loaded API: ${api.name} (${api.method})`, 'success');
    addLog(`📍 Endpoint: ${fullUrl}`, 'info');
    
    if (api.body) {
      addLog(`📦 Body template loaded - Replace values in {}`, 'info');
    }
  };

  // =====================================================
  // CURL Parser (Enhanced)
  // =====================================================

  const parseCurlCommand = (command) => {
    try {
      addLog('📝 Parsing CURL command...', 'info');
      
      let cleanCommand = command.replace(/\\\n/g, ' ').replace(/\n/g, ' ');
      
      // Extract URL
      const urlMatch = cleanCommand.match(/curl\s+(-X\s+\w+\s+)?["']?(https?:\/\/[^\s"'\)]+)/i);
      if (!urlMatch) {
        throw new Error('Could not extract URL from CURL command');
      }
      const extractedUrl = urlMatch[2];
      setUrl(extractedUrl);
      
      // Extract method
      const methodMatch = cleanCommand.match(/-X\s+(\w+)/i);
      if (methodMatch) {
        setMethod(methodMatch[1].toUpperCase());
      } else {
        setMethod('GET');
      }
      
      // Extract headers
      const headerRegex = /-H\s+['"]([^'"]+)['"]/g;
      const extractedHeaders = [];
      let headerMatch;
      while ((headerMatch = headerRegex.exec(cleanCommand)) !== null) {
        const [key, value] = headerMatch[1].split(': ');
        if (key && value) {
          extractedHeaders.push({ key: key.trim(), value: value.trim() });
        }
      }
      if (extractedHeaders.length > 0) {
        setHeaders(extractedHeaders);
      }
      
      // Extract JSON body
      const jsonMatch = cleanCommand.match(/-d\s+['"]({.*?})['"]/s);
      if (jsonMatch) {
        try {
          const jsonBody = JSON.parse(jsonMatch[1]);
          setBody(JSON.stringify(jsonBody, null, 2));
          setBodyType('json');
          addLog('✅ Parsed JSON body successfully', 'success');
        } catch (e) {
          setBody(jsonMatch[1]);
          setBodyType('raw');
        }
      }
      
      // Extract form data
      const formMatches = [...cleanCommand.matchAll(/-F\s+['"]([^'"]+)['"]/g)];
      if (formMatches.length > 0) {
        const forms = formMatches.map(m => {
          const [key, value] = m[1].split('=');
          return { key: key || '', value: value || '' };
        });
        setFormData(forms);
        setBodyType('form');
        addLog(`✅ Parsed ${forms.length} form fields`, 'success');
      }
      
      addLog('✅ CURL command parsed successfully', 'success');
    } catch (error) {
      addLog(`❌ Failed to parse CURL: ${error.message}`, 'error');
    }
  };

  // =====================================================
  // Execute Request
  // =====================================================

  const executeRequest = async () => {
    if (!url) {
      addLog('❌ Please provide a URL', 'error');
      return;
    }
    
    setIsRunning(true);
    setResponse(null);
    setResponseRaw('');
    setResponseStatus(null);
    
    addLog(`🚀 Executing ${method} request to: ${url}`, 'info');
    
    try {
      const requestHeaders = {};
      headers.forEach(h => {
        if (h.key && h.value) {
          requestHeaders[h.key] = h.value;
        }
      });
      
      let requestBody = null;
      if (method !== 'GET') {
        if (bodyType === 'json' && body) {
          try {
            requestBody = JSON.parse(body);
            requestHeaders['Content-Type'] = 'application/json';
          } catch (e) {
            requestBody = body;
          }
        } else if (bodyType === 'form') {
          const form = new FormData();
          formData.forEach(f => {
            if (f.key && f.value) {
              form.append(f.key, f.value);
            }
          });
          requestBody = form;
          delete requestHeaders['Content-Type'];
        } else if (bodyType === 'raw' && body) {
          requestBody = body;
        }
      }
      
      const fetchOptions = {
        method,
        headers: requestHeaders,
        credentials: 'omit',
      };
      
      if (requestBody) {
        fetchOptions.body = requestBody instanceof FormData ? requestBody : JSON.stringify(requestBody);
      }
      
      addLog(`📤 Sending request with headers: ${JSON.stringify(requestHeaders)}`, 'info');
      
      const fetchResponse = await fetch(url, fetchOptions);
      
      setResponseStatus({
        status: fetchResponse.status,
        statusText: fetchResponse.statusText,
        ok: fetchResponse.ok
      });
      
      const responseHeadersObj = {};
      fetchResponse.headers.forEach((value, key) => {
        responseHeadersObj[key] = value;
      });
      setResponseHeaders(responseHeadersObj);
      
      const contentType = fetchResponse.headers.get('content-type') || '';
      let responseData;
      let responseText;
      
      if (contentType.includes('application/json')) {
        responseData = await fetchResponse.json();
        responseText = JSON.stringify(responseData, null, 2);
        setResponse(responseData);
      } else {
        responseText = await fetchResponse.text();
        setResponseRaw(responseText);
        try {
          responseData = JSON.parse(responseText);
          setResponse(responseData);
        } catch {
          setResponse(responseText);
        }
      }
      
      setResponseRaw(responseText);
      
      if (fetchResponse.ok) {
        addLog(`✅ Request successful! Status: ${fetchResponse.status} ${fetchResponse.statusText}`, 'success');
      } else {
        addLog(`⚠️ Request completed with status: ${fetchResponse.status} ${fetchResponse.statusText}`, 'warn');
      }
      
      addLog(`📦 Response size: ${responseText.length} characters`, 'info');
      
    } catch (error) {
      addLog(`❌ Request failed: ${error.message}`, 'error');
      setResponse({ error: error.message });
      setResponseRaw(`Error: ${error.message}`);
      
      if (error.message.includes('fetch') || error.message.includes('CORS')) {
        addLog('💡 CORS error detected. Try using a CORS proxy.', 'warn');
      }
    } finally {
      setIsRunning(false);
    }
  };

  // =====================================================
  // Form Helpers
  // =====================================================

  const addHeader = () => {
    setHeaders([...headers, { key: '', value: '' }]);
  };

  const removeHeader = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...headers];
    newHeaders[index][field] = value;
    setHeaders(newHeaders);
  };

  const addFormField = () => {
    setFormData([...formData, { key: '', value: '' }]);
  };

  const removeFormField = (index) => {
    setFormData(formData.filter((_, i) => i !== index));
  };

  const updateFormField = (index, field, value) => {
    const newFormData = [...formData];
    newFormData[index][field] = value;
    setFormData(newFormData);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    addLog('📋 Copied to clipboard!', 'success');
  };

  // =====================================================
  // Example Commands
  // =====================================================

  const exampleCommands = [
    // Google Script APIs
    {
      name: "Get All Groups",
      command: `curl -X GET "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec?type=groups"`
    },
    {
      name: "Get User Info",
      command: `curl -X GET "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec?type=user_info&chatid=123456789"`
    },
    {
      name: "Get Leaderboard",
      command: `curl -X GET "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec?type=leaderboard&date=15/12/2024"`
    },
    {
      name: "Check & Add User",
      command: `curl -X POST "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"check_and_add","id":"123456789","isGroup":false,"name":"John Doe","username":"johndoe"}'`
    },
    {
      name: "Save User Response",
      command: `curl -X POST "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"user_and_group_response","userId":"123456789","groupId":"-1001234567890","isCorrect":true,"timestamp":"2024-12-15T12:00:00Z"}'`
    },
    {
      name: "Update Language",
      command: `curl -X POST "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"update_language","chatId":"-1001234567890","language":"hindi"}'`
    },
    {
      name: "Remove Chat IDs",
      command: `curl -X POST "https://script.google.com/macros/s/AKfycbyx6R8AMZM_GSEisXj6YkeuTo8YeKmdaL881gsxyivAA9qRxXLQZxrpeB3MX55Sp0IB/exec" \\
  -H "Content-Type: application/json" \\
  -d '{"type":"remove_chatids","chatIds":["123456789","987654321"],"isGroup":false}'`
    },
    
    // Telegram APIs
    {
      name: "Telegram: Get Me",
      command: `curl -X GET "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/getMe"`
    },
    {
      name: "Telegram: Send Message",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/sendMessage" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"123456789","text":"Hello from API!","parse_mode":"HTML"}'`
    },
    {
      name: "Telegram: Get Chat",
      command: `curl -X GET "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/getChat?chat_id=-1001234567890"`
    },
    {
      name: "Telegram: Get Admins",
      command: `curl -X GET "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/getChatAdministrators?chat_id=-1001234567890"`
    },
    {
      name: "Telegram: Export Invite Link",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/exportChatInviteLink" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"-1001234567890"}'`
    },
    {
      name: "Telegram: Promote User",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/promoteChatMember" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"-1001234567890","user_id":"987654321","can_delete_messages":true,"can_invite_users":true}'`
    },
    {
      name: "Telegram: Send Poll",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/sendPoll" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"-1001234567890","question":"What is 2+2?","options":["2","3","4","5"],"type":"quiz","correct_option_id":2}'`
    },
    {
      name: "Telegram: Ban User",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/banChatMember" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"-1001234567890","user_id":"987654321"}'`
    },
    {
      name: "Telegram: Leave Chat",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/leaveChat" \\
  -H "Content-Type: application/json" \\
  -d '{"chat_id":"-1001234567890"}'`
    },
    {
      name: "Telegram: Set Webhook",
      command: `curl -X POST "https://api.telegram.org/bot7745024625:AAEHsXyBWd7ekA7VBk15TG8xhCuzXyb3Zko/setWebhook" \\
  -H "Content-Type: application/json" \\
  -d '{"url":"https://your-webhook-url.com","max_connections":40}'`
    }
  ];

  const loadExample = (command) => {
    setCurlCommand(command);
    parseCurlCommand(command);
    addLog('📋 Example command loaded', 'info');
  };

  // API Categories for quick access
  const apiCategories = [
    {
      name: '📊 Google Script APIs',
      keys: ['groups', 'groupsAndQuestions', 'getAllChatIds', 'chatIdsAndQuestions', 
             'userInfo', 'groupInfo', 'combinedResponses', 'combinedResponsesGroups',
             'pollAndSettings', 'stats', 'leaderboard', 'weeklyRank', 'sheetData',
             'checkAndAdd', 'userGroupResponse', 'pollQuestion', 'updateLanguage',
             'updateFunny', 'removeChatIds']
    },
    {
      name: '🤖 Telegram Bot APIs',
      keys: ['telegramSendMessage', 'telegramEditMessage', 'telegramDeleteMessage',
             'telegramGetChat', 'telegramGetChatMembersCount', 'telegramGetChatMember',
             'telegramPromoteChatMember', 'telegramDemoteChatMember', 'telegramRestrictChatMember',
             'telegramBanChatMember', 'telegramUnbanChatMember', 'telegramSetChatPermissions',
             'telegramSetChatTitle', 'telegramSetChatDescription', 'telegramSetChatPhoto',
             'telegramDeleteChatPhoto', 'telegramPinChatMessage', 'telegramUnpinChatMessage',
             'telegramUnpinAllChatMessages', 'telegramLeaveChat', 'telegramGetChatAdministrators']
    },
    {
      name: '📨 Telegram Send APIs',
      keys: ['telegramSendPoll', 'telegramSendPhoto', 'telegramSendDocument',
             'telegramSendSticker', 'telegramSendVideo', 'telegramSendAudio',
             'telegramSendVoice', 'telegramSendLocation', 'telegramSendVenue',
             'telegramSendContact', 'telegramSendDice', 'telegramSendChatAction']
    },
    {
      name: '📋 Telegram Admin APIs',
      keys: ['telegramExportChatInviteLink', 'telegramCreateChatInviteLink',
             'telegramEditChatInviteLink', 'telegramRevokeChatInviteLink',
             'telegramApproveChatJoinRequest', 'telegramDeclineChatJoinRequest']
    },
    {
      name: '🔧 Telegram Bot APIs',
      keys: ['telegramGetMe', 'telegramGetUpdates', 'telegramSetWebhook',
             'telegramDeleteWebhook', 'telegramGetWebhookInfo', 'telegramForwardMessage',
             'telegramCopyMessage']
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800">
      {/* Header */}
      <header className="bg-gray-900/50 backdrop-blur-md border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-3">
              <div className="text-3xl">🚀</div>
              <h1 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-pink-400">
                CURL Command Runner
              </h1>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => navigate('/')}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-2 text-white"
              >
                <span>🏠</span> Back to Dashboard
              </button>
              <button
                onClick={logout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg transition-colors flex items-center gap-2 text-white"
              >
                <span>🚪</span> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Panel - Request Builder */}
          <div className="space-y-6">
            {/* Quick API Access */}
            <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-white">
                <span>⚡</span> Quick API Access
              </h2>
              
              {apiCategories.map((category, catIdx) => (
                <div key={catIdx} className="mb-4">
                  <h3 className="text-sm font-medium text-gray-400 mb-2">{category.name}</h3>
                  <div className="flex flex-wrap gap-2">
                    {category.keys.map((key) => {
                      const api = apiCollection[key];
                      return (
                        <button
                          key={key}
                          onClick={() => loadApiEndpoint(key)}
                          className="bg-gray-700 hover:bg-gray-600 text-xs px-3 py-1 rounded text-white transition-colors"
                          title={api?.description || ''}
                        >
                          {api?.name || key}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* CURL Input */}
            <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-white">
                <span>📝</span> CURL Command Input
              </h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium mb-2 text-white">Quick Examples:</label>
                <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                  {exampleCommands.map((example, idx) => (
                    <button
                      key={idx}
                      onClick={() => loadExample(example.command)}
                      className="bg-gray-700 hover:bg-gray-600 text-xs px-3 py-1 rounded text-white transition-colors"
                    >
                      {example.name}
                    </button>
                  ))}
                </div>
              </div>
              
              <textarea
                value={curlCommand}
                onChange={(e) => setCurlCommand(e.target.value)}
                placeholder={`Paste your CURL command here...`}
                rows="8"
                className="w-full bg-gray-900 border border-gray-600 rounded-lg px-4 py-3 text-green-400 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
              
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => parseCurlCommand(curlCommand)}
                  className="flex-1 bg-yellow-600 hover:bg-yellow-700 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200"
                >
                  🔍 Parse CURL
                </button>
                <button
                  onClick={() => setCurlCommand('')}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-4 rounded-lg transition-colors"
                >
                  Clear
                </button>
              </div>
            </div>

            {/* Manual Request Builder */}
            <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
              <h2 className="text-xl font-semibold mb-4 flex items-center gap-2 text-white">
                <span>🔧</span> Manual Request Builder
              </h2>
              
              <div className="space-y-4">
                <div className="flex gap-3">
                  <select
                    value={method}
                    onChange={(e) => setMethod(e.target.value)}
                    className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <option>GET</option>
                    <option>POST</option>
                    <option>PUT</option>
                    <option>DELETE</option>
                    <option>PATCH</option>
                  </select>
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://api.example.com/endpoint"
                    className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                
                {/* Headers */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <label className="text-sm font-medium text-white">Headers:</label>
                    <button onClick={addHeader} className="bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1 rounded text-white">
                      + Add Header
                    </button>
                  </div>
                  {headers.map((header, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="text"
                        placeholder="Header Name"
                        value={header.key}
                        onChange={(e) => updateHeader(idx, 'key', e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
                      />
                      <input
                        type="text"
                        placeholder="Value"
                        value={header.value}
                        onChange={(e) => updateHeader(idx, 'value', e.target.value)}
                        className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
                      />
                      <button onClick={() => removeHeader(idx)} className="text-red-400 hover:text-red-300">✕</button>
                    </div>
                  ))}
                </div>
                
                {/* Body Type Selector */}
                <div>
                  <label className="text-sm font-medium text-white mb-2 block">Body Type:</label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 text-white">
                      <input type="radio" value="json" checked={bodyType === 'json'} onChange={() => setBodyType('json')} />
                      <span>JSON</span>
                    </label>
                    <label className="flex items-center gap-2 text-white">
                      <input type="radio" value="form" checked={bodyType === 'form'} onChange={() => setBodyType('form')} />
                      <span>Form Data</span>
                    </label>
                    <label className="flex items-center gap-2 text-white">
                      <input type="radio" value="raw" checked={bodyType === 'raw'} onChange={() => setBodyType('raw')} />
                      <span>Raw Text</span>
                    </label>
                  </div>
                </div>
                
                {/* JSON Body */}
                {bodyType === 'json' && (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder='{"key": "value"}'
                    rows="6"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                )}
                
                {/* Form Data */}
                {bodyType === 'form' && (
                  <div>
                    {formData.map((field, idx) => (
                      <div key={idx} className="flex gap-2 mb-2">
                        <input
                          type="text"
                          placeholder="Field Name"
                          value={field.key}
                          onChange={(e) => updateFormField(idx, 'key', e.target.value)}
                          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
                        />
                        <input
                          type="text"
                          placeholder="Value"
                          value={field.value}
                          onChange={(e) => updateFormField(idx, 'value', e.target.value)}
                          className="flex-1 bg-gray-700 border border-gray-600 rounded-lg px-3 py-1 text-white text-sm"
                        />
                        <button onClick={() => removeFormField(idx)} className="text-red-400 hover:text-red-300">✕</button>
                      </div>
                    ))}
                    <button onClick={addFormField} className="bg-blue-600 hover:bg-blue-700 text-xs px-2 py-1 rounded text-white mt-2">
                      + Add Field
                    </button>
                  </div>
                )}
                
                {/* Raw Text Body */}
                {bodyType === 'raw' && (
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Raw text body..."
                    rows="6"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white font-mono text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                )}
              </div>
            </div>

            {/* Execute Button */}
            <button
              onClick={executeRequest}
              disabled={isRunning || !url}
              className="w-full py-4 rounded-xl font-bold text-lg transition-all text-white bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isRunning ? '⏳ Executing Request...' : '🚀 Execute Request'}
            </button>
          </div>

          {/* Right Panel - Response */}
          <div className="space-y-6">
            {/* Response Status */}
            {responseStatus && (
              <div className={`rounded-xl p-4 border ${responseStatus.ok ? 'bg-green-500/10 border-green-500' : 'bg-red-500/10 border-red-500'}`}>
                <div className="flex justify-between items-center">
                  <div>
                    <span className={`font-bold ${responseStatus.ok ? 'text-green-400' : 'text-red-400'}`}>
                      Status: {responseStatus.status} {responseStatus.statusText}
                    </span>
                  </div>
                  <button
                    onClick={() => copyToClipboard(responseRaw)}
                    className="text-gray-400 hover:text-white text-sm"
                  >
                    📋 Copy Response
                  </button>
                </div>
              </div>
            )}
            
            {/* Response Headers */}
            {Object.keys(responseHeaders).length > 0 && (
              <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
                <h3 className="text-lg font-semibold mb-3 text-white">Response Headers:</h3>
                <div className="bg-gray-900 rounded-lg p-3 font-mono text-xs overflow-x-auto max-h-40 overflow-y-auto">
                  {Object.entries(responseHeaders).map(([key, value]) => (
                    <div key={key} className="text-gray-300">
                      <span className="text-purple-400">{key}:</span> {value}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Response Body */}
            {(response || responseRaw) && (
              <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
                <h3 className="text-lg font-semibold mb-3 text-white">Response Body:</h3>
                <div className="bg-gray-900 rounded-lg p-4 font-mono text-xs overflow-x-auto max-h-96 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-green-400">
                    {typeof response === 'object' ? JSON.stringify(response, null, 2) : responseRaw}
                  </pre>
                </div>
              </div>
            )}
            
            {/* Logs Console */}
            <div className="bg-gray-800 rounded-xl shadow-xl border border-gray-700 p-6">
              <h3 className="text-lg font-semibold mb-3 text-white">📡 Execution Logs:</h3>
              <div ref={logContainerRef} className="bg-black/40 rounded-lg p-4 font-mono text-xs h-64 overflow-y-auto">
                {logs.length === 0 ? (
                  <div className="text-gray-500 text-center mt-20">
                    No logs yet. Execute a request to see logs.
                  </div>
                ) : (
                  logs.map((log, idx) => (
                    <div key={idx} className={`mb-2 pb-2 border-b border-gray-800 ${
                      log.type === 'error' ? 'text-red-400' : 
                      log.type === 'success' ? 'text-green-400' : 
                      log.type === 'warn' ? 'text-yellow-400' : 'text-gray-300'
                    }`}>
                      <span className="text-gray-500">[{log.timestamp}]</span> {log.message}
                    </div>
                  ))
                )}
              </div>
              <button onClick={() => setLogs([])} className="mt-3 text-gray-400 hover:text-white text-sm self-end">
                Clear Logs
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default CurlRunner;
